import { useEffect, useState } from 'react';
import { initChatwoot, identifyChatwootUser, resetChatwootIdentity } from '@/lib/chatwoot';
import { useProfile } from '@/contexts/profile-context';
import { emit } from '@/lib/analytics';
import { ChevronRight } from 'lucide-react';
import { Link, Outlet, useLocation, useParams, useSearchParams } from 'react-router';
import { AppShell } from '@/components/app-shell';
import { AppHeader, defaultHeaderActions } from '@/components/app-header';
import { orgUrl } from '@/lib/org-url';
import { workspaceUrl } from '@/lib/workspace-url';
import { isValidPublicId } from '@/lib/public-id';
import { AppSidebar } from '@/components/app-sidebar';
import { TeammateWelcomeModal } from '@/components/teammate-welcome-modal';
import { ActiveAnnouncement } from '@/components/announcements/active-announcement';
import { useWorkspace } from '@/contexts/workspace-context';
import { useFetch } from '@/hooks/use-fetch';
import { ConnectChannelModal } from '@/components/connect-channel-modal';
import type { WorkspacePublicId } from '@hookmyapp/shared';

/**
 * Phase 118 DashboardLayout -- the workspace provider is hoisted out (mounted
 * per route subtree in app.tsx). This component is the chrome (sidebar + header)
 * and mounts the Meta OAuth postMessage + storage listeners that navigate to
 * /w/:ws/channels/connecting when a popup or localStorage relay delivers a
 * {code, wsPublicId, state} payload.
 */
export function DashboardLayout() {
  const { activeWorkspace } = useWorkspace();
  const fetchWithAuth = useFetch();
  const { profile } = useProfile();

  // Support widget (Oscar): load once, re-identify when the profile arrives.
  useEffect(() => {
    initChatwoot();
    if (profile) identifyChatwootUser(profile, fetchWithAuth);
    else resetChatwootIdentity();
  }, [profile, fetchWithAuth]);

  // Connect target = the URL's :ws, ALWAYS. activeWorkspace is a cached,
  // localStorage-reconciled context value that can lag or fall back to the
  // org's team workspace while the workspace list is stale (fresh customer);
  // using it as the target connected channels to the wrong workspace
  // (AIT-207). The route param is what the user is looking at.
  const { ws } = useParams();
  const connectTargetWs =
    ws && isValidPublicId(ws, 'ws') ? (ws as WorkspacePublicId) : activeWorkspace?.id;

  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const handleConnectClick = () => {
    emit('connect_channel_modal_opened', {});
    setConnectModalOpen(true);
  };

  // ?connect=1 opens the chooser on arrival (RootRedirect forwards the query
  // into /w/:ws). Open it once and strip the param so it doesn't re-trigger on
  // refresh or workspace switches. NOTE: invited signups no longer come
  // through here -- they get the standalone /invite/connect flow, which never
  // enters a workspace (invite-connect.ts).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('connect') !== '1') return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot query-param consumption, not a render cascade (same pattern as workspace-context.tsx:242)
    setConnectModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('connect');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Customer scope: header breadcrumb (org › customer) is the back navigation.
  // The org crumb returns to where the user came from (the route that opened
  // this customer, threaded via nav state), defaulting to the org's Customers
  // list; the customer crumb links to the customer's own home (channels/
  // dashboard) so sub-pages can step back up.
  const location = useLocation();
  const cameFrom = (location.state as { from?: string } | null)?.from;
  const orgPublicId = activeWorkspace?.organizationPublicId;
  const customerCrumb =
    activeWorkspace?.kind === 'customer' && orgPublicId && isValidPublicId(orgPublicId, 'org') ? (
      <nav className="flex min-w-0 items-center gap-1.5 text-sm" aria-label="Breadcrumb">
        <Link
          to={cameFrom ?? orgUrl(orgPublicId, 'customers')}
          className="text-muted-foreground transition-colors hover:text-foreground"
        >
          {activeWorkspace.organizationName}
        </Link>
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
        <Link
          to={workspaceUrl(activeWorkspace.id)}
          className="max-w-[40vw] truncate font-medium hover:underline"
        >
          {activeWorkspace.name}
        </Link>
      </nav>
    ) : undefined;

  return (
    <AppShell
      sidebar={<AppSidebar onConnectClick={handleConnectClick} />}
      header={<AppHeader left={customerCrumb} actions={defaultHeaderActions()} />}
      beforeChrome={
        <>
          <TeammateWelcomeModal />
          <ActiveAnnouncement />
        </>
      }
      afterChrome={
        <ConnectChannelModal
          open={connectModalOpen}
          onOpenChange={setConnectModalOpen}
          workspacePublicId={connectTargetWs as WorkspacePublicId | undefined}
          fetchWithAuth={fetchWithAuth}
        />
      }
    >
      <Outlet context={{ onConnectClick: handleConnectClick }} />
    </AppShell>
  );
}
